import React, { useState } from 'react';
import { Layout, Table, Input, Dropdown, DatePicker, Button, Modal } from '../components/common';

const ProductMasterDemo = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRows, setSelectedRows] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    description: '',
    uom: '',
    weight: '',
    length: '',
    width: '',
    height: '',
    date: '',
    batchControlled: 'yes',
  });

  // Sample product data
  const [products, setProducts] = useState([
    {
      sku: 'SKU-12345',
      description: 'Industrial Component',
      uom: 'PCS',
      weight: 2.5,
      dimensions: '10x8x5',
      batchControlled: 'Yes',
      date: '2026-01-15',
    },
    {
      sku: 'SKU-12346',
      description: 'Electronic Module',
      uom: 'BOX',
      weight: 5.2,
      dimensions: '20x15x10',
      batchControlled: 'No',
      date: '2026-01-20',
    },
    {
      sku: 'SKU-12347',
      description: 'Mechanical Part',
      uom: 'KG',
      weight: 1.8,
      dimensions: '5x5x3',
      batchControlled: 'Yes',
      date: '2026-01-18',
    },
  ]);

  const columns = [
    { key: 'sku', label: 'SKU', sortable: true },
    { key: 'description', label: 'Description', sortable: true },
    { key: 'uom', label: 'UOM', sortable: true, align: 'center' },
    { key: 'weight', label: 'Weight (Kg)', sortable: true, align: 'center' },
    { key: 'dimensions', label: 'Dimensions (LxBxH)', sortable: false },
    { key: 'batchControlled', label: 'Batch Controlled', sortable: true, align: 'center' },
    {
      key: 'actions',
      label: 'Actions',
      align: 'center',
      render: (value, row) => (
        <div className="flex gap-2 justify-center">
          <button className="text-blue-600 hover:text-blue-800 font-medium text-sm">✏️ Edit</button>
          <button className="text-red-600 hover:text-red-800 font-medium text-sm">🗑️ Delete</button>
        </div>
      ),
    },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddProduct = () => {
    const newProduct = {
      sku: `SKU-${Date.now()}`,
      description: formData.description,
      uom: formData.uom,
      weight: parseFloat(formData.weight),
      dimensions: `${formData.length}x${formData.width}x${formData.height}`,
      batchControlled: formData.batchControlled === 'yes' ? 'Yes' : 'No',
      date: formData.date,
    };
    
    setProducts([...products, newProduct]);
    setFormData({
      description: '',
      uom: '',
      weight: '',
      length: '',
      width: '',
      height: '',
      date: '',
      batchControlled: 'yes',
    });
    setShowModal(false);
  };

  const uomOptions = [
    { value: 'pcs', label: 'PCS' },
    { value: 'box', label: 'BOX' },
    { value: 'kg', label: 'KG' },
    { value: 'ltr', label: 'LTR' },
    { value: 'mtr', label: 'MTR' },
  ];

  const batchOptions = [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
  ];

  return (
    <Layout activeNav="products" onNavChange={() => {}}>
      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Product Master</h1>
        <p className="text-gray-600">Manage and view all product details in one place.</p>
      </div>

      {/* Search and Create Button */}
      <div className="flex gap-4 mb-6">
        <div className="flex-1">
          <Input
            placeholder="Search by ID"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon="🔍"
          />
        </div>
        <Button
          variant="primary"
          onClick={() => setShowModal(true)}
        >
          + Create New Product
        </Button>
      </div>

      {/* Table */}
      <Table
        columns={columns}
        data={products}
        selectable={true}
        selectedRows={selectedRows}
        onSelectRow={setSelectedRows}
        onSelectAll={setSelectedRows}
        sortable={true}
        emptyMessage="No products found"
      />

      {/* Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title="Add New Product"
        size="lg"
      >
        <div className="space-y-5">
          {/* Product Description */}
          <div>
            <label className="label">Product Description</label>
            <Input
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Enter description"
              required
            />
          </div>

          {/* UOM and Weight */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Unit of Measure</label>
              <Dropdown
                options={uomOptions}
                value={formData.uom}
                onChange={(val) => setFormData(prev => ({ ...prev, uom: val }))}
                placeholder="Select"
              />
            </div>
            <div>
              <label className="label">Weight (Kg)</label>
              <Input
                name="weight"
                type="number"
                value={formData.weight}
                onChange={handleInputChange}
                placeholder="Enter weight"
              />
            </div>
          </div>

          {/* Dimensions */}
          <div>
            <label className="label">Dimensions (cm)</label>
            <div className="grid grid-cols-3 gap-3">
              <Input
                name="length"
                type="number"
                value={formData.length}
                onChange={handleInputChange}
                placeholder="Length"
              />
              <Input
                name="width"
                type="number"
                value={formData.width}
                onChange={handleInputChange}
                placeholder="Breath"
              />
              <Input
                name="height"
                type="number"
                value={formData.height}
                onChange={handleInputChange}
                placeholder="Height"
              />
            </div>
          </div>

          {/* Date */}
          <DatePicker
            label="Date"
            value={formData.date}
            onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
          />

          {/* Batch Controlled */}
          <div>
            <label className="label">Batch Controlled</label>
            <Dropdown
              options={batchOptions}
              value={formData.batchControlled}
              onChange={(val) => setFormData(prev => ({ ...prev, batchControlled: val }))}
              placeholder="Select"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 justify-end mt-8">
            <Button
              variant="outline"
              onClick={() => setShowModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="success"
              onClick={handleAddProduct}
            >
              Create
            </Button>
          </div>
        </div>
      </Modal>
    </Layout>
  );
};

export default ProductMasterDemo;
